/**
 * QR scanning for the clock-in screen.
 *
 * - Android Chrome: the native BarcodeDetector.
 * - iPhone (and anything else without native QR support): the barcode-detector ponyfill
 *   (ZXing compiled to WebAssembly). It is loaded lazily, and its .wasm file is bundled
 *   with the app so it is served from our own origin (and precached for offline use) —
 *   never from a CDN.
 */
// A URL string only: the ~1 MB .wasm itself is fetched on first use of the ponyfill.
import wasmUrl from "zxing-wasm/reader/zxing_reader.wasm?url";

export interface QrDetector {
  /** Decoded payloads found in the image (may be empty). */
  detect(source: HTMLCanvasElement): Promise<string[]>;
}

interface NativeBarcodeDetector {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
}
interface NativeBarcodeDetectorCtor {
  new (opts: { formats: string[] }): NativeBarcodeDetector;
  getSupportedFormats(): Promise<string[]>;
}

let detectorPromise: Promise<QrDetector> | null = null;
/** Drops the WebAssembly decoder's own cached (possibly failed) instance. */
let purgeWasm: (() => void) | null = null;

/** Returns a (cached) QR detector, preferring the browser's own. */
export function getQrDetector(): Promise<QrDetector> {
  detectorPromise ??= createDetector().catch((e) => {
    detectorPromise = null; // allow a retry next time
    throw e;
  });
  return detectorPromise;
}

/** Forgets the cached detector so the next getQrDetector() builds (and loads) a new one. */
export function resetQrDetector(): void {
  detectorPromise = null;
  purgeWasm?.();
}

async function createDetector(): Promise<QrDetector> {
  const Native = (globalThis as { BarcodeDetector?: NativeBarcodeDetectorCtor }).BarcodeDetector;
  if (Native) {
    try {
      const formats = await Native.getSupportedFormats();
      if (formats.includes("qr_code")) {
        const d = new Native({ formats: ["qr_code"] });
        return { detect: async (src) => (await d.detect(src)).map((b) => b.rawValue) };
      }
    } catch {
      // Fall through to the WebAssembly decoder.
    }
  }
  const { BarcodeDetector, prepareZXingModule, purgeZXingModule } = await import("barcode-detector/ponyfill");
  purgeWasm = purgeZXingModule;
  // Load and compile the .wasm now (while the camera starts), so a failed download shows
  // "The scanner didn't load" instead of a live camera that silently never decodes.
  try {
    await prepareZXingModule({
      overrides: {
        locateFile: (path: string, prefix: string) => (path.endsWith(".wasm") ? wasmUrl : prefix + path),
      },
      fireImmediately: true,
    });
  } catch (e) {
    purgeZXingModule(); // the failure is cached otherwise, and "Try again" would never work
    throw e;
  }
  const d = new BarcodeDetector({ formats: ["qr_code"] });
  return { detect: async (src) => (await d.detect(src)).map((b) => b.rawValue) };
}

/** The decoder keeps failing on every frame: it is broken, not just a blurry frame. */
export class ScannerError extends Error {}

// --- Camera ----------------------------------------------------------------

export type CameraProblem = "denied" | "no_camera" | "in_use" | "unsupported" | "other";

export class CameraError extends Error {
  constructor(
    public problem: CameraProblem,
    message: string,
  ) {
    super(message);
  }
}

/** Opens the rear camera. Throws CameraError with a plain-English problem kind. */
export async function openRearCamera(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new CameraError("unsupported", "This browser can't use the camera.");
  }
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
    });
  } catch (e) {
    const name = (e as { name?: string }).name ?? "";
    if (name === "NotAllowedError" || name === "SecurityError") {
      throw new CameraError("denied", "Camera permission was not given.");
    }
    if (name === "NotFoundError" || name === "OverconstrainedError") {
      throw new CameraError("no_camera", "No camera was found.");
    }
    if (name === "NotReadableError" || name === "AbortError") {
      throw new CameraError("in_use", "The camera is being used by another app.");
    }
    throw new CameraError("other", "The camera couldn't be started.");
  }
}

export function stopStream(stream: MediaStream | null | undefined): void {
  for (const t of stream?.getTracks() ?? []) t.stop();
}

// --- Scan loop -------------------------------------------------------------

const SCAN_INTERVAL_MS = 250;
/** Fraction of the shorter video side used for the centre crop (matches the on-screen frame). */
const CROP_FRACTION = 0.75;
/** Downscale the crop to at most this many pixels square — plenty for a 5 cm sticker. */
const MAX_CROP_PX = 640;
/** This many decoder failures in a row means the decoder itself is broken. */
const MAX_CONSECUTIVE_ERRORS = 8;

/**
 * Decodes a centre crop of the video a few times a second until `onPayload` returns
 * true (accepted) or `signal` aborts. A failure on a single frame is ignored, but a run
 * of them throws ScannerError (e.g. the WebAssembly decoder couldn't be loaded).
 */
export async function scanVideo(
  video: HTMLVideoElement,
  detector: QrDetector,
  onPayload: (raw: string) => boolean,
  signal: AbortSignal,
): Promise<void> {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Canvas not available");
  let errors = 0;
  while (!signal.aborted) {
    const started = performance.now();
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (w > 0 && h > 0 && video.readyState >= 2) {
      const side = Math.floor(Math.min(w, h) * CROP_FRACTION);
      const out = Math.min(side, MAX_CROP_PX);
      canvas.width = out;
      canvas.height = out;
      ctx.drawImage(video, (w - side) / 2, (h - side) / 2, side, side, 0, 0, out, out);
      try {
        const codes = await detector.detect(canvas);
        if (signal.aborted) return;
        errors = 0;
        for (const raw of codes) if (onPayload(raw)) return;
      } catch (e) {
        if (signal.aborted) return;
        // Usually just a bad frame; try the next one. But not forever.
        if (++errors >= MAX_CONSECUTIVE_ERRORS) {
          throw new ScannerError(
            `The QR decoder keeps failing: ${e instanceof Error ? e.message : String(e)}`,
          );
        }
      }
    }
    const wait = Math.max(0, SCAN_INTERVAL_MS - (performance.now() - started));
    await new Promise((r) => setTimeout(r, wait));
  }
}

// --- Payload ---------------------------------------------------------------

const TOKEN_RE = /^[A-Za-z0-9_-]{22}$/;

/**
 * Returns the site token if the payload looks like one of our stickers: a
 * https://<app>/c/<22-char token> URL or a bare token. Otherwise null.
 * The server does the real check.
 */
export function parseClockCode(raw: string): string | null {
  const trimmed = raw.trim();
  if (TOKEN_RE.test(trimmed)) return trimmed;
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    const m = url.pathname.match(/^\/c\/([A-Za-z0-9_-]{22})\/?$/);
    return m?.[1] ?? null;
  } catch {
    return null;
  }
}

export function isClockToken(s: string | undefined | null): s is string {
  return !!s && TOKEN_RE.test(s);
}
